import React, { useState, useEffect } from 'react';
import { ref, onValue, push, get, update } from 'firebase/database';
import { db } from '../firebase';

interface Pickup {
  id: string;
  name: string;
  address: string;
  pickupTime: string;
  timestamp: number;
  isCompleted: boolean;
  orderNumber: number;
  driverId?: string;  // Optional field for driver assignment
  dropoffTime?: string;
}

const ShuttleScheduler = () => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [pickupTime, setPickupTime] = useState('17:00');
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [timeSlots, setTimeSlots] = useState<{ [key: string]: number }>({});

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 17; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    options.push('00:00');
    return options;
  };

  const isTimeSlotDisabled = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const currentMinutes = hours * 60 + minutes;
    
    // Check if time is before or after 17:45 (5:45 PM)
    const cutoffTime = 17 * 60 + 45; // 17:45 in minutes
    const maxSlots = currentMinutes <= cutoffTime ? 1 : 2;
    
    // Check current slot capacity
    if (timeSlots[time] >= maxSlots) return true;

    // Check previous slot capacity
    const previousSlotMinutes = currentMinutes - 15;
    const previousHours = Math.floor(previousSlotMinutes / 60);
    const previousMinutes = previousSlotMinutes % 60;
    const previousTimeSlot = `${previousHours.toString().padStart(2, '0')}:${previousMinutes.toString().padStart(2, '0')}`;
    const previousMaxSlots = previousSlotMinutes <= cutoffTime ? 1 : 2;

    if (timeSlots[previousTimeSlot] >= previousMaxSlots) return true;

    return false;
  };

  useEffect(() => {
    console.log('Initializing database connection...');
    const pickupsRef = ref(db, 'pickups');

    const unsubscribe = onValue(pickupsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const pickupList = Object.entries(data).map(([id, pickup]: [string, any]) => ({
          id,
          ...pickup
        }));

        const slots: { [key: string]: number } = {};
        pickupList
          .filter(pickup => !pickup.isCompleted)
          .forEach(pickup => {
            slots[pickup.pickupTime] = (slots[pickup.pickupTime] || 0) + 1;
          });

        setTimeSlots(slots);
        setPickups(pickupList
          .filter(pickup => !pickup.isCompleted)
          .sort((a, b) => {
            const [aHours, aMinutes] = a.pickupTime.split(':').map(Number);
            const [bHours, bMinutes] = b.pickupTime.split(':').map(Number);
            const aTime = aHours * 60 + aMinutes;
            const bTime = bHours * 60 + bMinutes;
            return aTime - bTime;
          }));
      } else {
        setTimeSlots({});
        setPickups([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateOrderNumbers = async () => {
    const pickupsRef = ref(db, 'pickups');
    const snapshot = await get(pickupsRef);
    const currentPickups = snapshot.val() || {};
    
    const sortedPickups = Object.entries(currentPickups)
      .map(([id, pickup]: [string, any]) => ({ id, ...pickup }))
      .filter(pickup => !pickup.isCompleted)
      .sort((a, b) => {
        const [aHours, aMinutes] = a.pickupTime.split(':').map(Number);
        const [bHours, bMinutes] = b.pickupTime.split(':').map(Number);
        const aTime = aHours * 60 + aMinutes;
        const bTime = bHours * 60 + bMinutes;
        return aTime - bTime;
      });

    const updates: { [key: string]: number } = {};
    sortedPickups.forEach((pickup, index) => {
      updates[`pickups/${pickup.id}/orderNumber`] = index + 1;
    });

    const dbRef = ref(db);
    await update(dbRef, updates);
  };

  const addPickup = async () => {
    if (!name.trim() || !address.trim() || !pickupTime) {
      alert('Please fill in all fields');
      return;
    }
    
    try {
      const pickupsRef = ref(db, 'pickups');
      const newPickup = {
        name: name.trim(),
        address: address.trim(),
        pickupTime,
        timestamp: Date.now(),
        isCompleted: false,
        orderNumber: 1,
        driverId: ''
      };
      
      await push(pickupsRef, newPickup);
      await updateOrderNumbers();
      
      setName('');
      setAddress('');
      setPickupTime('17:00');
    } catch (error) {
      console.error('Error adding pickup:', error);
      alert('Failed to add pickup. Please try again.');
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <>
      <div style={{
        backgroundColor: '#4C4CFF',
        height: '60px',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 20px'
      }}>
        <span style={{
          color: 'white',
          fontSize: 'clamp(0.8rem, 4vw, 1.2rem)',
          fontWeight: '700',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center'
        }}>
          Foundation Ventures X Airfoil Investors
        </span>
      </div>

      <div style={{
        position: 'fixed',
        top: '80px',
        left: '20px',
        zIndex: 1001
      }}>
        <img 
          src="/noderLogo.png" 
          alt="Noder Logo" 
          style={{
            height: 'clamp(50px, 10vw, 100px)',
            width: 'auto'
          }}
        />
      </div>
      
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '80px 20px 20px',
        color: '#1a1a1a',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
          textAlign: 'center',
          marginBottom: '0.5rem',
          marginTop: '60px',
          fontWeight: '700'
        }}>
          Driver Scheduling
        </h1>
        
        <p style={{
          textAlign: 'center',
          fontSize: 'clamp(0.875rem, 3vw, 1rem)',
          color: '#666',
          marginBottom: '1.5rem'
        }}>
          Request your pickup in seconds. Track your position in the queue instantly.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
          gap: '20px',
          alignItems: 'start'
        }}>
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
              marginBottom: '1rem',
              fontWeight: '600'
            }}>Request Pickup</h2>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '10px'
            }}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                style={{ 
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: 'clamp(0.875rem, 3vw, 1rem)',
                  backgroundColor: 'white',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Example: Floor, Block/Tower, Building Name, Street Number & Name, District, Hong Kong"
                style={{ 
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                  backgroundColor: 'white',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
              <select
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                style={{ 
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: 'clamp(0.875rem, 3vw, 1rem)',
                  backgroundColor: 'white',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {generateTimeOptions().map((time) => (
                  <option 
                    key={time} 
                    value={time}
                    disabled={isTimeSlotDisabled(time)}
                    style={{
                      color: isTimeSlotDisabled(time) ? '#999' : 'inherit'
                    }}
                  >
                    {time}
                  </option>
                ))}
              </select>
              <button 
                onClick={addPickup}
                style={{
                  padding: '14px',
                  backgroundColor: '#4C4CFF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.875rem, 3vw, 1rem)',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  marginTop: '10px',
                  width: '100%'
                }}
              >
                Request Pickup
              </button>
            </div>
          </div>

          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
              marginBottom: '1rem',
              fontWeight: '600'
            }}>Current Queue</h2>
            
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              {pickups.length === 0 ? (
                <p style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  color: '#666',
                  margin: 0,
                  fontSize: 'clamp(0.875rem, 3vw, 1rem)'
                }}>No pickups in queue</p>
              ) : (
                pickups
                  .filter(pickup => !pickup.isCompleted)
                  .map(pickup => (
                    <div 
                      key={pickup.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '15px 20px',
                        borderBottom: '1px solid #eee',
                        fontSize: 'clamp(0.875rem, 3vw, 1.1rem)'
                      }}
                    >
                      <span style={{ fontWeight: '500' }}>
                        #{pickup.orderNumber} - {pickup.name}
                      </span>
                      <span style={{ color: '#4CAF50' }}>
                        {formatTime(pickup.pickupTime)}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShuttleScheduler; 